import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, lazy, Suspense } from "react";
import { ArrowRight, Phone, MessageCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { contact } from "@/data/content";
import { scrollToHash } from "@/components/Navbar";
import { useLang } from "@/i18n/LanguageContext";
import { useSiteContent } from "@/i18n/SiteContentContext";
import { sendInquiryEmail } from "@/lib/emailjs";

const Truck3D = lazy(() => import("@/components/Truck3D").then((m) => ({ default: m.Truck3D })));
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { get } = useSiteContent();
  const [qName, setQName] = useState("");
  const [qPhone, setQPhone] = useState("");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const s = get("stats", null);
  const heroStats = [
    { value: s?.experience ?? 20, suffix: "+", label: t("hero.stat1") },
    { value: s?.fleet ?? 100, suffix: "+", label: t("hero.stat2") },
    { value: 6, suffix: "", label: t("hero.stat3") },
    { value: s?.deliveries ?? 5000, suffix: "+", label: t("hero.stat4") },
  ];

  const quickSubmit = async (e) => {
    e.preventDefault();
    try {
      await sendInquiryEmail({ name: qName, phone: qPhone, service: "Quick Inquiry — Callback Request" });
      await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: qName, phone: qPhone, source: "quick-inquiry" }),
      });
      toast.success("Inquiry sent — our team will call you back shortly.");
      setQName("");
      setQPhone("");
    } catch {
      toast.error("Could not send right now — please try the contact form below.");
    }
  };

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden bg-gradient-to-b from-mist via-white to-ice/40">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src="/assets/fleet/fleet-02.jpg" alt="" className="h-full w-full object-cover opacity-[0.14]" />
        <div className="absolute inset-0 bg-gradient-to-b from-mist/70 via-white/80 to-ice/60" />
        <div className="honeycomb-bg absolute inset-0 opacity-70" />
        <div className="absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-sky/20 blur-3xl" />
        <div className="absolute -left-40 bottom-0 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-3xl" />
      </motion.div>

      <motion.div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pt-32 lg:px-10 lg:pt-24">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="order-2" data-testid="hero-truck-wrap">
            <div className="relative mx-auto h-52 w-full max-w-lg sm:h-80 lg:h-[30rem]">
              <div className="absolute inset-x-8 bottom-4 h-24 rounded-full bg-royal/10 blur-2xl" />
              <Suspense fallback={<div className="mx-auto mt-16 h-40 w-40 animate-pulse rounded-full bg-royal/10" />}>
                <Truck3D />
              </Suspense>
            </div>
          </div>

          <div className="order-1 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-6 inline-flex max-w-full items-center gap-3 rounded-full border border-royal/15 bg-white/70 px-4 py-2 backdrop-blur-md sm:px-5"
            >
              <span className="h-2 w-2 shrink-0 animate-pulse-node rounded-full bg-leaf" />
              <span className="font-jakarta text-[10px] font-bold uppercase tracking-[0.15em] text-royal sm:text-xs sm:tracking-[0.25em]">{t("hero.badge")}</span>
            </motion.div>

            <h1 className="font-jakarta text-5xl font-extrabold leading-[0.95] tracking-tight text-navy sm:text-6xl lg:text-7xl xl:text-8xl">
              <MaskedLine delay={0.35}>MANDAIR</MaskedLine>
              <MaskedLine delay={0.5}>
                <span className="text-outline">MOVERS</span>
              </MaskedLine>
              <MaskedLine delay={0.65}>
                <span className="text-xl font-bold tracking-normal text-royal sm:text-3xl lg:text-4xl">General Transport L.L.C — S.P.C</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-6 max-w-md text-base leading-relaxed text-royal/80 md:text-lg"
            >
              {t("hero.tagline")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button data-testid="hero-explore-btn" onClick={() => scrollToHash("#services")} className="btn-premium">
                {t("hero.explore")} <ArrowRight className="h-4 w-4" />
              </button>
              <button data-testid="hero-contact-btn" onClick={() => scrollToHash("#contact")} className="btn-outline-premium">
                <Phone className="h-4 w-4" /> {t("hero.contact")}
              </button>
            </motion.div>

            <motion.form
              onSubmit={quickSubmit}
              data-testid="quick-inquiry-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="glass-card mt-6 flex max-w-lg flex-col gap-3 rounded-2xl p-4 sm:flex-row sm:items-center"
            >
              <span className="font-jakarta text-xs font-extrabold uppercase tracking-widest text-royal/60 sm:whitespace-nowrap">{t("quick.title")}</span>
              <input required data-testid="quick-name-input" value={qName} onChange={(e) => setQName(e.target.value)} placeholder={t("quick.name")} className="w-full rounded-xl border border-royal/15 bg-white/80 px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/20" />
              <input required data-testid="quick-phone-input" value={qPhone} onChange={(e) => setQPhone(e.target.value)} placeholder={t("quick.phone")} className="w-full rounded-xl border border-royal/15 bg-white/80 px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/20" />
              <button type="submit" data-testid="quick-submit-btn" className="btn-premium !px-5 !py-2.5 sm:whitespace-nowrap">
                <MessageCircle className="h-4 w-4" /> {t("quick.submit")}
              </button>
            </motion.form>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-royal/10 bg-royal/10 shadow-xl shadow-royal/5 md:grid-cols-4"
          data-testid="hero-stats"
        >
          {heroStats.map((s) => (
            <div key={s.label} className="bg-white/80 px-6 py-6 backdrop-blur-md">
              <p className="font-jakarta text-3xl font-extrabold text-navy md:text-4xl">
                {s.value.toLocaleString()}{s.suffix}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-royal/60">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative z-10 flex justify-center pb-8 pt-10"
      >
        <div className="flex flex-col items-center gap-3 text-navy">
          <span className="font-jakarta text-sm font-extrabold uppercase tracking-[0.3em] text-navy/70 md:text-base">{t("hero.scroll")}</span>
          <motion.span animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} className="grid h-11 w-11 place-items-center rounded-full border-2 border-gold bg-white/80 shadow-lg shadow-gold/20">
            <ChevronDown className="h-5 w-5 text-gold" />
          </motion.span>
        </div>
      </motion.div>
    </section>
  );
};
